import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { format } from 'date-fns'
import { catchError, Observable, tap, throwError } from 'rxjs'
import { PARSE_DATE_PROPS } from 'src/app/shared/interceptors/date-interceptor'
import { GLOBAL_LOADING_SPINNER_ENABLED } from 'src/app/shared/interceptors/loading-spinner-interceptor'
import { APIEnvelope } from 'src/app/shared/models/api'
import { DownloadService } from 'src/app/shared/services/download.service'
import { est, formatAsUTC } from 'src/app/shared/utilities/date'
import {
  filter,
  FilterParams,
  httpParams,
  pagination,
  PaginationParams,
  sort,
  SortParams,
  withHttpParams,
} from 'src/app/shared/utilities/http-params'
import { ToastService } from '../toasts/toasts.service'
import { 
  CreateServicePointRequest, 
  UpsertFlagsRequest, 
  ServicePointEffectiveDatedAttribute, 
  UpsertAttributesRequest, 
  ServicePointAttribute,
  SpaggAttribute ,
} from './model'

@Injectable({ providedIn: 'root' })
export class ServicePointManagementService {
  http = inject(HttpClient)
  downloadService = inject(DownloadService)
  toastService = inject(ToastService)

  createServicePoint(req: CreateServicePointRequest) {
    return this.http
      .post('service_points/', req)
      .pipe(
        catchError(
          this.notifyError(
            'An error occurred while creating a service point',
          ).bind(this),
        ),
      )
  }

  upsertFlags(servicePointId: string, req: UpsertFlagsRequest) {
    return this.http
      .post(`service_points/${servicePointId}/flags`, req)
      .pipe(
        catchError(
          this.notifyError(
            'An error occurred while creating a service point flags',
          ).bind(this),
        ),
      )
  }

  getFlags(
    page?: PaginationParams,
    filters?: FilterParams[],
    order?: SortParams[],
  ) {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<ServicePointEffectiveDatedAttribute>>(
      'service_points/flags',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, [
            'effective_date',
            'termination_date',
            'create_time',
            'last_update_time',
          ]),
      },
    )
  }

  exportFlags(filters?: FilterParams[]) {
    const params = httpParams(withHttpParams(filter, filters))
    return this.http
      .get<string[]>('service_points/flags/export', {
        params,
        context: new HttpContext().set(PARSE_DATE_PROPS, [
          'effective_date',
          'termination_date',
          'create_time',
          'last_update_time',
        ]),
      })
      .pipe(
        tap((urls) => {
          const timestamp = format(est(), 't')
          this.downloadService.download(
            urls.map((url, i) => ({
              url,
              name: `interval-usage-export-${i}-${timestamp}.csv`,
            })),
          )
        }),
        catchError(
          this.notifyError('An error occurred while exporting flags').bind(this),
        ),
      )
  }

  deleteFlag = (service_point_id: string, effective_date: Date) => {
    return this.http
      .delete(
        `service_points/${service_point_id}/${formatAsUTC(
          effective_date,
        )}/flags`,
      )
      .pipe(
        catchError(
          this.notifyError('An error occurred while deleting flags').bind(this),
        ),
      )
  }

  upsertAttributes(servicePointId: string, req: UpsertAttributesRequest) {
    return this.http
      .post(`service_points/${servicePointId}/effective`, req)
      .pipe(
        catchError(
          this.notifyError(
            'An error occurred while creating a service point overrides',
          ).bind(this),
        ),
      )
  }

  getAttributes(
    page?: PaginationParams,
    filters?: FilterParams[],
    order?: SortParams[],
    showLoading = false,
  ): Observable<APIEnvelope<ServicePointAttribute>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<ServicePointAttribute>>(
      'service_points/effective',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, showLoading)
          .set(PARSE_DATE_PROPS, [
            'effective_date',
            'termination_date',
            'create_time',
            'last_update_time',
          ]),
      },
    )
  }

  exportAttributes(filters?: FilterParams[]) {
    const params = httpParams(withHttpParams(filter, filters))
    return this.http
      .get<string[]>('service_points/effective/export', {
        params,
        context: new HttpContext().set(PARSE_DATE_PROPS, [
          'effective_date',
          'termination_date',
          'create_time',
          'last_update_time',
        ]),
      })
      .pipe(
        tap((urls) => {
          const timestamp = format(est(), 't')
          this.downloadService.download(
            urls.map((url, i) => ({
              url,
              name: `interval-usage-export-${i}-${timestamp}.csv`,
            })),
          )
        }),
        catchError(
          this.notifyError('An error occurred while exporting overrides').bind(
            this,
          ),
        ),
      )
  }

  deleteAttribute = (service_point_id: string, effective_date: Date) => {
    return this.http
      .delete(
        `service_points/${service_point_id}/${formatAsUTC(
          effective_date,
        )}/effective`,
      )
      .pipe(
        catchError(
          this.notifyError('An error occurred while deleting overrides').bind(
            this,
          ),
        ),
      )
  }

  getSpaggAtrributes(
    page?: PaginationParams,
    filters?: FilterParams[],
    order?: SortParams[],
    showLoading = false,
  ) {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<SpaggAttribute>>(
      'spagg_attributes',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, showLoading)
          .set(PARSE_DATE_PROPS, [
            'effective_date',
            'termination_date',
            'create_time',
            'last_update_time',
          ]),
      },
    )
  }
  notifyError(message: string) {
    return (error: unknown) => {
      // eslint-disable-next-line no-console
      console.error(error)
      this.toastService.show({ message, type: 'error' })
      return throwError(() => error)
    }
  }
}
